import { FunctionComponent, useRef, useState } from "react";
import {
	Box,
	ClickAwayListener,
	IconButton,
	ListItemIcon,
	ListItemText,
	MenuItem,
	MenuList,
	Paper,
	Popper,
} from "@mui/material";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import ContentCopyOutlinedIcon from "@mui/icons-material/ContentCopyOutlined";
import CheckIcon from "@mui/icons-material/Check";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import TranslateIcon from "@mui/icons-material/Translate";
import {
	colorDark,
	colorGrayDark,
	colorGrayLight,
	colorGuava,
} from "../../styles/theme";
import BasketIcon from "../Icons/Basket";

interface Props {
	onCopyFullText?: () => Promise<void>;
	translateOn?: boolean;
	onToggleTranslate?: () => void;
	translateLoading?: boolean;
	isInBasket: boolean;
	onAddToBasket: () => void;
}

type CopyState = "idle" | "loading" | "copied" | "error";

const COPY_LABELS: Record<CopyState, string> = {
	idle: "Copy full text",
	loading: "Copying…",
	copied: "Copied",
	error: "Copy failed, try again",
};

const Spinner = () => (
	<Box
		sx={{
			width: 16,
			height: 16,
			borderRadius: "50%",
			border: "2px solid rgba(127, 127, 135, 0.3)",
			borderTopColor: colorGrayDark,
			boxSizing: "border-box",
			animation: "cardHeadSpin 0.7s linear infinite",
			"@keyframes cardHeadSpin": {
				"0%": { transform: "rotate(0deg)" },
				"100%": { transform: "rotate(360deg)" },
			},
		}}
	/>
);

const CardActionsMenu: FunctionComponent<Props> = ({
	onCopyFullText,
	translateOn,
	onToggleTranslate,
	translateLoading,
	isInBasket,
	onAddToBasket,
}) => {
	const anchorRef = useRef<HTMLButtonElement>(null);
	const [open, setOpen] = useState(false);
	const [copyState, setCopyState] = useState<CopyState>("idle");

	const closeMenu = () => setOpen(false);

	const handleClickAway = (event: MouseEvent | TouchEvent) => {
		if (anchorRef.current?.contains(event.target as Node)) return;
		closeMenu();
	};

	const handleCopy = async () => {
		if (copyState === "loading") return;
		setCopyState("loading");
		try {
			await onCopyFullText();
			setCopyState("copied");
			closeMenu();
			setTimeout(() => setCopyState("idle"), 2000);
		} catch {
			setCopyState("error");
			setTimeout(() => setCopyState("idle"), 3000);
		}
	};

	const handleToggleTranslate = () => {
		onToggleTranslate();
		closeMenu();
	};

	const handleAddToBasket = () => {
		onAddToBasket();
		closeMenu();
	};

	return (
		<>
			<IconButton
				ref={anchorRef}
				onClick={() => setOpen((prev) => !prev)}
				aria-label="Document actions"
				aria-haspopup="true"
				aria-expanded={open}
				sx={{
					width: "32px",
					height: "32px",
					flexShrink: 0,
					borderRadius: "10px",
					color: open ? colorDark : colorGrayDark,
					backgroundColor: open ? colorGrayLight : "transparent",
				}}
			>
				<MoreVertIcon sx={{ width: 20, height: 20 }} />
			</IconButton>
			<Popper
				open={open}
				anchorEl={anchorRef.current}
				placement="bottom-end"
				disablePortal
				sx={{ zIndex: 2 }}
				modifiers={[{ name: "offset", options: { offset: [0, 4] } }]}
			>
				<ClickAwayListener onClickAway={handleClickAway}>
					<Paper
						sx={{
							borderRadius: "10px !important",
							minWidth: "210px",
							overflow: "hidden",
							boxShadow: "0 4px 16px rgba(0, 0, 0, 0.15)",
						}}
					>
						<MenuList
							dense
							onKeyDown={(e) => {
								if (e.key === "Escape") closeMenu();
							}}
						>
							{onCopyFullText && (
								<MenuItem
									onClick={handleCopy}
									disabled={copyState === "loading"}
								>
									<ListItemIcon>
										{copyState === "loading" ? (
											<Spinner />
										) : copyState === "copied" ? (
											<CheckIcon
												sx={{ width: 20, height: 20, color: "#2e7d32" }}
											/>
										) : copyState === "error" ? (
											<ErrorOutlineIcon
												sx={{ width: 20, height: 20, color: colorGuava }}
											/>
										) : (
											<ContentCopyOutlinedIcon
												sx={{ width: 20, height: 20, color: colorGrayDark }}
											/>
										)}
									</ListItemIcon>
									<ListItemText
										primaryTypographyProps={{
											fontSize: 14,
											color: copyState === "error" ? colorGuava : undefined,
										}}
										primary={COPY_LABELS[copyState]}
									/>
								</MenuItem>
							)}
							{onToggleTranslate && (
								<MenuItem onClick={handleToggleTranslate}>
									<ListItemIcon>
										{translateLoading ? (
											<Spinner />
										) : (
											<TranslateIcon
												sx={{
													width: 20,
													height: 20,
													color: translateOn ? colorDark : colorGrayDark,
												}}
											/>
										)}
									</ListItemIcon>
									<ListItemText
										primaryTypographyProps={{ fontSize: 14 }}
										primary={
											translateOn ? "Show original" : "Translate to English"
										}
									/>
								</MenuItem>
							)}
							<MenuItem onClick={handleAddToBasket} selected={isInBasket}>
								<ListItemIcon>
									<BasketIcon sx={{ width: 20, height: 19 }} />
								</ListItemIcon>
								<ListItemText
									primaryTypographyProps={{ fontSize: 14 }}
									primary={isInBasket ? "In the letter" : "Add to letter"}
								/>
							</MenuItem>
						</MenuList>
					</Paper>
				</ClickAwayListener>
			</Popper>
		</>
	);
};

export default CardActionsMenu;
